import React, { useState, useRef, useEffect } from 'react';
import { Box, Text, VStack, UnorderedList, ListItem } from '@chakra-ui/react';
import { getCustomFieldValueType } from '../../../utils/tableUtils';

/**
 * Rich text editor for custom fields that maintains visual formatting.
 * Supports editing arrays and objects with document-like interface.
 *
 * Keyboard shortcuts:
 * - Shift+Enter (once): New line within same item
 * - Shift+Enter (twice): New item in array
 * - For objects: "key: value" format (key up to 5 words followed by colon)
 *
 * @param {Object} props
 * @param {*} props.value - The value to edit
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.size - Size variant ('sm', 'md', 'lg')
 */
const CustomFieldEditor = ({ value, onChange, size = 'md' }) => {
  const valueType = getCustomFieldValueType(value);
  const [editableContent, setEditableContent] = useState([]);
  const containerRef = useRef(null);
  const editableRefs = useRef({});
  const isUserEditingRef = useRef(false);

  const fontSize = size === 'lg' ? 'md' : 'sm';

  const textColor = '#a3a3a3';
  const keyColor = '#fafafa';
  const emptyColor = 'rgba(255,255,255,0.2)';

  const prevValueStringRef = useRef('');

  useEffect(() => {
    const currentValueString = JSON.stringify(value);

    if (editableContent.length === 0) {
      initializeContent();
      prevValueStringRef.current = currentValueString;
      return;
    }

    if (isUserEditingRef.current) return;

    if (prevValueStringRef.current !== currentValueString) {
      initializeContent();
      prevValueStringRef.current = currentValueString;
    }
  }, [value]);

  const initializeContent = () => {
    switch (valueType) {
      case 'array':
        if (Array.isArray(value)) {
          const content =
            value.length > 0
              ? value.map((item, idx) => ({
                  id: `item-${idx}`,
                  value: String(item),
                  type: 'array-item',
                }))
              : [{ id: 'item-0', value: '', type: 'array-item' }];
          setEditableContent(content);
        }
        break;

      case 'array-of-objects':
        if (Array.isArray(value)) {
          const content =
            value.length > 0
              ? value.map((obj, idx) => ({
                  id: `obj-${idx}`,
                  value: obj,
                  type: 'object-item',
                }))
              : [{ id: 'obj-0', value: '', type: 'object-item' }];
          setEditableContent(content);
        }
        break;

      case 'object':
        setEditableContent([{ id: 'object-root', value, type: 'object' }]);
        break;

      default:
        setEditableContent([]);
    }
  };

  const parseObjectText = (text) => {
    const lines = text.split('\n');
    const result = {};
    let currentKey = null;
    let currentValue = [];

    for (const line of lines) {
      const keyMatch = line.match(/^([^\s:]+(?:\s+[^\s:]+){0,4})\s*:\s*(.*)$/);
      if (keyMatch) {
        if (currentKey !== null) {
          result[currentKey] = currentValue.join('\n').trim();
        }
        currentKey = keyMatch[1].trim();
        currentValue = [keyMatch[2]];
      } else if (currentKey !== null) {
        currentValue.push(line);
      }
    }

    if (currentKey !== null) {
      result[currentKey] = currentValue.join('\n').trim();
    }

    return result;
  };

  const serializeContentFromItems = (items) => {
    switch (valueType) {
      case 'array':
        return items.filter((item) => item.value.trim() !== '').map((item) => item.value);

      case 'array-of-objects':
        return items
          .map((item) =>
            typeof item.value === 'object' ? item.value : parseObjectText(item.value)
          )
          .filter((obj) => Object.keys(obj).length > 0);

      case 'object':
        if (items.length > 0) {
          return typeof items[0].value === 'object'
            ? items[0].value
            : parseObjectText(items[0].value);
        }
        return {};

      default:
        return value;
    }
  };

  const lastShiftEnterRef = useRef(0);

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      const now = Date.now();
      const timeSinceLastShiftEnter = now - lastShiftEnterRef.current;

      if (timeSinceLastShiftEnter < 500 && timeSinceLastShiftEnter > 0) {
        lastShiftEnterRef.current = 0;
        isUserEditingRef.current = true;

        const text = e.target.textContent;
        const newContent = [...editableContent];
        newContent[index].value = text.replace(/\n+$/, '');

        const newId = `item-${Date.now()}`;
        const newItem = {
          id: newId,
          value: '',
          type: valueType === 'array-of-objects' ? 'object-item' : 'array-item',
        };

        newContent.splice(index + 1, 0, newItem);
        setEditableContent(newContent);

        setTimeout(() => {
          const newElement = containerRef.current?.querySelector(`[data-id="${newId}"]`);
          if (newElement) {
            newElement.focus();
            const range = document.createRange();
            const sel = window.getSelection();
            range.setStart(newElement, 0);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }, 10);
      } else {
        lastShiftEnterRef.current = now;
        document.execCommand('insertLineBreak');
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  };

  const notifyParentChangeTimeout = useRef(null);

  const handleInput = (e, index) => {
    isUserEditingRef.current = true;
    const newValue = e.target.textContent;
    const newContent = [...editableContent];
    newContent[index].value = newValue;
    setEditableContent(newContent);

    if (notifyParentChangeTimeout.current) {
      clearTimeout(notifyParentChangeTimeout.current);
    }
    notifyParentChangeTimeout.current = setTimeout(() => {
      const serialized = serializeContentFromItems(newContent);
      onChange(serialized);
      prevValueStringRef.current = JSON.stringify(serialized);
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (notifyParentChangeTimeout.current) {
        clearTimeout(notifyParentChangeTimeout.current);
      }
    };
  }, []);

  const handleKeyUp = (e, index) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const text = e.target.textContent;
      if (text.trim() === '' && editableContent.length > 1) {
        isUserEditingRef.current = true;
        const newContent = editableContent.filter((_, idx) => idx !== index);
        setEditableContent(newContent);

        if (notifyParentChangeTimeout.current) {
          clearTimeout(notifyParentChangeTimeout.current);
        }
        const serialized = serializeContentFromItems(newContent);
        onChange(serialized);
        prevValueStringRef.current = JSON.stringify(serialized);

        setTimeout(() => {
          isUserEditingRef.current = false;
        }, 100);

        const prevIndex = Math.max(0, index - 1);
        setTimeout(() => {
          const prevElement =
            containerRef.current?.querySelectorAll('[contenteditable]')[prevIndex];
          if (prevElement) prevElement.focus();
        }, 0);
      }
    }
  };

  const focusCss = {
    outline: 'none',
    '&:focus': {
      backgroundColor: 'rgba(255,255,255,0.04)',
      borderRadius: '4px',
      padding: '2px 4px',
    },
  };

  switch (valueType) {
    case 'null':
      return (
        <Text fontSize={fontSize} color={emptyColor}>
          —
        </Text>
      );

    case 'boolean':
    case 'number':
    case 'string':
      return null;

    case 'array':
      return (
        <Box ref={containerRef}>
          <UnorderedList spacing={2} ml={4} mt={1}>
            {editableContent.map((item, idx) => (
              <ListItem key={item.id} fontSize={fontSize} color={textColor}>
                <Box
                  as="span"
                  ref={(el) => {
                    if (el) {
                      editableRefs.current[item.id] = el;
                      if (el !== document.activeElement && el.textContent !== item.value) {
                        el.textContent = item.value;
                      }
                    }
                  }}
                  contentEditable
                  suppressContentEditableWarning
                  data-id={item.id}
                  onInput={(e) => handleInput(e, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  onKeyUp={(e) => handleKeyUp(e, idx)}
                  color={textColor}
                  css={{
                    outline: 'none',
                    minWidth: '100px',
                    display: 'inline-block',
                    whiteSpace: 'pre-wrap',
                    ...focusCss['&:focus'] && { '&:focus': focusCss['&:focus'] },
                  }}
                />
              </ListItem>
            ))}
          </UnorderedList>
        </Box>
      );

    case 'array-of-objects':
      return (
        <Box ref={containerRef}>
          <UnorderedList spacing={3} ml={4} mt={1}>
            {editableContent.map((item, idx) => {
              const displayText =
                typeof item.value === 'object'
                  ? Object.entries(item.value)
                      .map(([key, val]) => `${key}: ${val}`)
                      .join('\n')
                  : item.value;

              return (
                <ListItem key={item.id} fontSize={fontSize} color={textColor}>
                  <VStack align="start" spacing={0.5}>
                    <Box
                      ref={(el) => {
                        if (el) {
                          editableRefs.current[item.id] = el;
                          if (el !== document.activeElement && el.textContent !== displayText) {
                            el.textContent = displayText;
                          }
                        }
                      }}
                      contentEditable
                      suppressContentEditableWarning
                      data-id={item.id}
                      onInput={(e) => handleInput(e, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      onKeyUp={(e) => handleKeyUp(e, idx)}
                      w="100%"
                      color={textColor}
                      css={{
                        outline: 'none',
                        minHeight: '20px',
                        whiteSpace: 'pre-wrap',
                        '&:focus': {
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          borderRadius: '4px',
                          padding: '4px',
                        },
                      }}
                    />
                  </VStack>
                </ListItem>
              );
            })}
          </UnorderedList>
        </Box>
      );

    case 'object': {
      if (editableContent.length === 0) return null;

      const displayText =
        typeof editableContent[0].value === 'object'
          ? Object.entries(editableContent[0].value)
              .map(([key, val]) => `${key}: ${val}`)
              .join('\n')
          : editableContent[0].value;

      return (
        <Box ref={containerRef}>
          <VStack align="start" spacing={1} mt={1}>
            <Box
              ref={(el) => {
                if (el) {
                  editableRefs.current[editableContent[0].id] = el;
                  if (el !== document.activeElement && el.textContent !== displayText) {
                    el.textContent = displayText;
                  }
                }
              }}
              contentEditable
              suppressContentEditableWarning
              data-id={editableContent[0].id}
              onInput={(e) => handleInput(e, 0)}
              w="100%"
              color={textColor}
              fontSize={fontSize}
              css={{
                outline: 'none',
                minHeight: '20px',
                whiteSpace: 'pre-wrap',
                '&:focus': {
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: '4px',
                  padding: '4px',
                },
              }}
            />
          </VStack>
        </Box>
      );
    }

    default:
      return null;
  }
};

export default CustomFieldEditor;
